import { formatClock, queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import { durationText, numberText, offsetText, shareText } from './format'
import { buildTraceReport } from './report'
import type {
  AqsiRegisterCheck,
  AqsiTraceDeepState,
  AqsiTraceNetwork,
  AqsiTraceRegisterSettings,
  AqsiTraceSale,
  AqsiTraceSaleItem,
  AqsiTraceSales,
  TracePointRowVm,
  TraceSaleRowVm,
  TraceSaleVm,
  TraceScreenVm,
  TraceFailureVm,
  TraceSettingRowVm,
  TraceToolsVm,
  TraceTrendVm,
  TraceWaterfallRowVm,
  TraceSegment,
} from './types'
import { SEGMENT_WORDS, SETTING_NOTES, SOURCE_WORDS, segmentLabel, stageWords } from './words'

/** Минимум точек, с которого тренд «кабинет → касса» имеет смысл рисовать. */
export const TREND_MIN_POINTS = 5
const DAY_MS = 24 * 60 * 60 * 1000
const MIN_BAR_PERCENT = 1.5
const CABINET_TO_REGISTER = 'cabinet-to-register'

const yesNo = (value: boolean | null): string => (value === null ? 'не передано' : value ? 'да' : 'нет')

const momentText = (iso: string): string => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/** Название участка, на который ушло больше всего времени. */
export function dominantLabel(key: string, segments: readonly TraceSegment[]): string {
  const known = SEGMENT_WORDS[key]?.label
  if (known !== undefined) return known
  const own = segments.find((segment) => segment.key === key)
  return own?.label ?? key
}

export function waterfallOf(segments: readonly TraceSegment[], dominant: string): readonly TraceWaterfallRowVm[] {
  const total = segments.reduce((sum, segment) => sum + segment.ms, 0)
  const top = segments.some((segment) => segment.key === dominant)
    ? dominant
    : segments.reduce<TraceSegment | null>((best, segment) => (best === null || segment.ms > best.ms ? segment : best), null)?.key
  let passed = 0
  return segments.map((segment) => {
    const offsetPercent = total > 0 ? (passed / total) * 100 : 0
    const widthPercent = total > 0 ? Math.max((segment.ms / total) * 100, MIN_BAR_PERCENT) : 0
    passed += segment.ms
    return {
      key: segment.key,
      label: segmentLabel(segment.key, segment.label),
      durationText: durationText(segment.ms),
      shareText: shareText(segment.ms, total),
      offsetPercent: Math.min(offsetPercent, 100 - MIN_BAR_PERCENT),
      widthPercent,
      dominant: segment.key === top,
    }
  })
}

export function summaryOf(segments: readonly TraceSegment[], dominant: string): string | null {
  const total = segments.reduce((sum, segment) => sum + segment.ms, 0)
  const top =
    segments.find((segment) => segment.key === dominant) ??
    segments.reduce<TraceSegment | null>((best, segment) => (best === null || segment.ms > best.ms ? segment : best), null)
  if (top === null || total <= 0) return null
  return `Больше всего времени ушло на: ${segmentLabel(top.key, top.label)} — ${durationText(top.ms)} (${shareText(top.ms, total)})`
}

function pointsOf(sale: AqsiTraceSale): readonly TracePointRowVm[] {
  const first = sale.points.reduce((min, point) => Math.min(min, Date.parse(point.at)), Number.POSITIVE_INFINITY)
  return sale.points.map((point, index) => ({
    id: `${index}:${point.source}:${point.stage}`,
    sourceText: SOURCE_WORDS[point.source],
    stageText: stageWords(point.source, point.stage),
    offsetText: Number.isFinite(first) ? offsetText(Date.parse(point.at) - first) : '—',
    durationText: durationText(point.durationMs),
    outcome: point.outcome ?? '',
  }))
}

export function networkTextOf(network: AqsiTraceNetwork | null): string | null {
  if (network === null) return null
  const parts: string[] = []
  if (network.effectiveType !== null) parts.push(`тип ${network.effectiveType}`)
  if (network.rttMs !== null) parts.push(`отклик сети ${durationText(network.rttMs)}`)
  if (network.downlinkMbps !== null) parts.push(`скорость ${numberText(network.downlinkMbps)} Мбит/с`)
  return parts.length === 0 ? null : `Сеть кассира: ${parts.join(', ')}`
}

export function settingsOf(settings: AqsiTraceRegisterSettings | null): readonly TraceSettingRowVm[] {
  if (settings === null) return []
  const note = (slows: boolean | null, text: string): string | null => (slows === true ? `может замедлять чек: ${text}` : null)
  const count = settings.acquiringSlipsCount
  return [
    {
      key: 'acquiringSlipsCount',
      label: 'Копии слипов эквайринга на кассе',
      valueText: count === null ? 'не передано' : String(count),
      slowsNote: note(count === null ? null : count > 0, SETTING_NOTES.acquiringSlipsCount),
    },
    {
      key: 'receiptCompactCheck',
      label: 'Компактная печать чека',
      valueText: yesNo(settings.receiptCompactCheck),
      slowsNote: note(settings.receiptCompactCheck === null ? null : !settings.receiptCompactCheck, SETTING_NOTES.receiptCompactCheck),
    },
    {
      key: 'hasAdvertisingLines',
      label: 'Рекламные строки на чеке',
      valueText: yesNo(settings.hasAdvertisingLines),
      slowsNote: note(settings.hasAdvertisingLines, SETTING_NOTES.hasAdvertisingLines),
    },
    {
      key: 'acquiringLockFn',
      label: 'Отключать питание накопителя при эквайринге',
      valueText: yesNo(settings.acquiringLockFn),
      slowsNote: note(settings.acquiringLockFn, SETTING_NOTES.acquiringLockFn),
    },
    {
      key: 'isStandbyModeEnabled',
      label: 'Режим ожидания команд',
      valueText: yesNo(settings.isStandbyModeEnabled),
      slowsNote: note(settings.isStandbyModeEnabled, SETTING_NOTES.isStandbyModeEnabled),
    },
    {
      key: 'displayTurningOffTimeoutMillis',
      label: 'Выключение экрана через',
      valueText:
        settings.displayTurningOffTimeoutMillis === null ? 'не передано' : durationText(settings.displayTurningOffTimeoutMillis),
      slowsNote: null,
    },
    {
      key: 'modeAcquiringTerminalEnable',
      label: 'Эквайринг на терминале включён',
      valueText: yesNo(settings.modeAcquiringTerminalEnable),
      slowsNote: null,
    },
    {
      key: 'isCardQrCombined',
      label: 'Карта и QR СБП на одном экране',
      valueText: yesNo(settings.isCardQrCombined),
      slowsNote: null,
    },
  ]
}

function rowOf(item: AqsiTraceSaleItem, selectedId: string | null): TraceSaleRowVm {
  return {
    saleId: item.saleId,
    startedAt: item.startedAt,
    startedText: momentText(item.startedAt),
    outcome: item.outcome,
    totalText: durationText(item.totalMs),
    dominantText: dominantLabel(item.dominant, item.segments),
    selected: selectedId !== null && item.saleId.toLowerCase() === selectedId.toLowerCase(),
  }
}

/**
 * Тренд «кабинет → касса» за сутки: это и есть «качество интернета самой кассы». Берутся загруженные продажи последних суток
 * (от самой свежей); меньше {@link TREND_MIN_POINTS} точек — тренда нет, а не пустая картинка.
 */
export function trendOf(items: readonly AqsiTraceSaleItem[]): TraceTrendVm | null {
  const timed = items
    .map((item) => ({ at: Date.parse(item.startedAt), ms: item.segments.find((segment) => segment.key === CABINET_TO_REGISTER)?.ms }))
    .filter((point): point is { at: number; ms: number } => Number.isFinite(point.at) && point.ms !== undefined)
  if (timed.length === 0) return null
  const newest = Math.max(...timed.map((point) => point.at))
  const day = timed.filter((point) => newest - point.at <= DAY_MS).sort((left, right) => left.at - right.at)
  if (day.length < TREND_MIN_POINTS) return null
  const sorted = day.map((point) => point.ms).sort((left, right) => left - right)
  const middle = Math.floor(sorted.length / 2)
  const median = sorted.length % 2 === 1 ? (sorted[middle] ?? 0) : ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
  return {
    values: day.map((point) => point.ms),
    medianText: durationText(median),
    maxText: durationText(sorted[sorted.length - 1] ?? 0),
  }
}

/** Подробный режим и замер кассы: что прочитано и что идёт сейчас. */
export interface TraceToolsInput {
  deep: AqsiTraceDeepState | null
  deepBusy: boolean
  deepError: TraceFailureVm | null
  check: AqsiRegisterCheck | null
  checkBusy: boolean
  checkError: TraceFailureVm | null
}

export const NO_TOOLS: TraceToolsInput = { deep: null, deepBusy: false, deepError: null, check: null, checkBusy: false, checkError: null }

export function toolsOf(input: TraceToolsInput): TraceToolsVm {
  const deep = input.deep
  const check = input.check
  const until = deep === null || deep.until === null ? null : formatClock(deep.until)
  return {
    deep: {
      known: deep !== null,
      enabled: deep?.enabled === true,
      text:
        deep === null
          ? 'Состояние подробного режима не прочитано'
          : deep.enabled
            ? `Подробный режим включён${until === null ? '' : ` до ${until}`} — выключится сам`
            : 'Подробный режим выключен',
      busy: input.deepBusy,
      error: input.deepError,
    },
    check: {
      busy: input.checkBusy,
      error: input.checkError,
      runs: (check?.runs ?? []).map((run, index) => ({
        id: String(index),
        text: `Замер ${index + 1}: доставка ${durationText(run.deliveryMs)}, ответ ${durationText(run.totalMs)} — ${run.outcome}`,
      })),
      medianText:
        check === null || check.medianDeliveryMs === null ? null : `Медиана доставки: ${durationText(check.medianDeliveryMs)}`,
    },
  }
}

export interface TraceVmInput {
  tools?: TraceToolsInput
  sales: QueryResultLike<AqsiTraceSales> | undefined
  /** Разбор выбранной продажи; `undefined` — ничего не выбрано. */
  sale: QueryResultLike<AqsiTraceSale> | undefined
  saleId: string | null
}

const EMPTY_VIEW: QueryView = { status: 'idle', refreshing: false, error: null }

export function saleVmOf(sale: AqsiTraceSale | undefined, query: QueryView, saleId: string, listed: AqsiTraceSaleItem | undefined): TraceSaleVm {
  if (sale === undefined) {
    return {
      saleId,
      query,
      totalText: listed === undefined ? '—' : durationText(listed.totalMs),
      summaryText: null,
      waterfall: [],
      points: [],
      networkText: null,
      settings: [],
      detailCount: 0,
      report: null,
    }
  }
  const total = sale.segments.reduce((sum, segment) => sum + segment.ms, 0)
  const vm: TraceSaleVm = {
    saleId,
    query,
    totalText: durationText(listed?.totalMs ?? total),
    summaryText: summaryOf(sale.segments, sale.dominant),
    waterfall: waterfallOf(sale.segments, sale.dominant),
    points: pointsOf(sale),
    networkText: networkTextOf(sale.network),
    settings: settingsOf(sale.registerSettings),
    detailCount: sale.detail.length,
    report: null,
  }
  return { ...vm, report: buildTraceReport(vm, listed) }
}

/** Модель экрана из результатов запросов (`endpoint.select(args)`): данные берутся только в состоянии «готово». */
export function buildTraceVm(input: TraceVmInput): TraceScreenVm {
  const listQuery =
    input.sales === undefined ? EMPTY_VIEW : queryView(input.sales, (data: AqsiTraceSales) => data.items.length === 0)
  const items = listQuery.status === 'ready' ? (input.sales?.data?.items ?? []) : []
  let sale: TraceSaleVm | null = null
  const selectedId = input.saleId
  if (selectedId !== null) {
    const query =
      input.sale === undefined
        ? EMPTY_VIEW
        : queryView(input.sale, (data: AqsiTraceSale) => data.segments.length === 0 && data.points.length === 0)
    const data = query.status === 'ready' || query.status === 'empty' ? input.sale?.data : undefined
    const listed = items.find((item) => item.saleId.toLowerCase() === selectedId.toLowerCase())
    sale = saleVmOf(data, query, selectedId, listed)
  }
  return {
    list: { query: listQuery, rows: items.map((item) => rowOf(item, selectedId)) },
    trend: trendOf(items),
    sale,
    tools: toolsOf(input.tools ?? NO_TOOLS),
  }
}
